import { getDimensionsOfHidden } from '../helpers/getDimensionsOfHidden';
import { RevealPanel } from "./revealPanel";
import { throttle, debounce } from 'lodash';

const TRIGGER_ATTRIBUTE = "data-subnav-dropdown-trigger";
const PANEL_ATTRIBUTE = "data-subnav-dropdown-panel";
const DESKTOP_LIST_ATTRIBUTE = "data-subnav-desktop";
const MOBILE_LIST_ATTRIBUTE = "data-subnav-mobile";
const DROPDOWN_UI_CLASS = "use-dropdown";
const PRELOAD_CLASS = "pdp-subnav-preload";
const TICKETS_ANCHOR = "buy-tickets";

type SubnavSection = {
    links: HTMLAnchorElement[];
    name: string;
    section: HTMLElement;
    position: number
}
export class OnPageSubnav {
    container: HTMLElement;
    sections: SubnavSection[];
    highlightedSection: SubnavSection | undefined;
    triggerElement: HTMLButtonElement;
    panel: RevealPanel | undefined;
    isMobile: boolean;
    desktopLinkList: HTMLElement;
    positionBuffer: number = 30;
    debouncedResizeHandler: (event: UIEvent) => void;

    constructor(container: HTMLElement) {
        this.container = container;
        this.triggerElement = container.querySelector(`[${TRIGGER_ATTRIBUTE}]`) as HTMLButtonElement;
        const panelElement = container.querySelector(`[${PANEL_ATTRIBUTE}]`) as HTMLElement;
        const mobileContainer = container.querySelector(`[${MOBILE_LIST_ATTRIBUTE}]`) as HTMLElement;
        this.desktopLinkList = container.querySelector(`[${DESKTOP_LIST_ATTRIBUTE}]`) as HTMLElement;
        this.sections = [];
        this.isMobile = false;
        let links: HTMLAnchorElement[] = [];

        if (!!panelElement) {
            links = Array.from(panelElement.querySelectorAll('a[href^="#"]')) as HTMLAnchorElement[];
            const ticketLink = mobileContainer?.querySelector(`a[href="#${TICKETS_ANCHOR}"]`) as HTMLAnchorElement;
            if (ticketLink) {
                links = links.concat(ticketLink);
            }
            this.panel = new RevealPanel(panelElement);
        }

        links.forEach((link, index) => {
            const urlHash = new URL(link.href)?.hash;
            const associatedSection = document.querySelector(`${urlHash}`) as HTMLElement;

            if (urlHash && associatedSection) {
                const allLinks = Array.from(this.container.querySelectorAll(`a[href$="${urlHash}"]`)) as HTMLAnchorElement[];
                const sectionInfo = {
                    links: allLinks,
                    name: link.textContent?.trim(),
                    section: associatedSection,
                    position: associatedSection.offsetTop - this.positionBuffer
                }
                this.sections.push(sectionInfo);
            }
            link.addEventListener('click', () => {
                if (!!this.panel) {
                    this.panel.closePanel();
                }
            })
        });

        if (this.sections.length > 0) {
            this.sections.sort((a, b) => {
                return a.position - b.position;
            });
            this.updateSection(this.sections[0]);
        }

        this.debouncedResizeHandler = debounce(this.handleResize.bind(this), 200);
        window.addEventListener('scroll', throttle(this.handleScroll.bind(this), 50));
        window.addEventListener('resize', this.debouncedResizeHandler);
        this.setPositions();
        this.chooseLinkDisplay();
    }

    handleScroll() {
        const scroll = window.scrollY;
        let selectedSection = this.sections[0];
        this.sections.forEach(sect => {
            if (scroll > sect.position) {
                selectedSection = sect;
            }
        });
        this.updateSection(selectedSection);
    }

    handleResize() {
        this.setPositions();
        this.chooseLinkDisplay();
    }

    setPositions() {
        // get nav height
        this.positionBuffer = this.container.offsetHeight ?? 30;
        document.documentElement.style.setProperty('--subnav-clearance', `${this.positionBuffer}px`);
        // Store position of sections to check on scroll
        this.sections.forEach(sect => {
            sect.position = sect.section.offsetTop - this.positionBuffer - 15; // small extra buffer
        });
    }

    updateSection(activeSection: SubnavSection) {
        // Update UI to reflect current scrolled section
        this.highlightedSection = activeSection;
        this.sections.forEach(section => {
            if (section.section == this.highlightedSection?.section) {
                section.links.forEach(link => {
                    link.setAttribute('aria-current', 'true');
                });
                this.triggerElement.innerText = section.name;
            }
            else {
                section.links.forEach(link => {
                    link.removeAttribute('aria-current');
                });
            }
        })
    }

    chooseLinkDisplay() {
        // Swap between link list & dropdown menu depending on available space
        const linkContainerWidth = this.desktopLinkList.parentElement!.offsetWidth;
        const linkListSize = getDimensionsOfHidden(this.desktopLinkList, 'flex');
        if (linkContainerWidth && linkListSize.width && linkContainerWidth <= (linkListSize.width + 10)) {
            if (!this.container.classList.contains(DROPDOWN_UI_CLASS)) {
                this.container.classList.add(DROPDOWN_UI_CLASS);
            }
        }
        else if (this.container.classList.contains(DROPDOWN_UI_CLASS)) {
            this.container.classList.remove(DROPDOWN_UI_CLASS);
        }
        if (this.container.classList.contains(PRELOAD_CLASS)) {
            this.container.classList.remove(PRELOAD_CLASS);
        }
    }
}